import { useMemo, useEffect, useState } from 'react';
import { GetAnalyticsTraitsQuery } from './__generated__/queries.types';
import {
  UserChangeHandler,
  GroupChangeHandler,
  PageChangeHandler,
} from './types';

export type Props = {
  fetching: boolean;
  data?: GetAnalyticsTraitsQuery;
  isSpoofing?: boolean;
  location?: string;
  onUserChange: UserChangeHandler;
  onGroupChange: GroupChangeHandler;
  onPageChange: PageChangeHandler;
};

export default function useIdentifier({
  data,
  fetching,
  isSpoofing,
  location,
  onUserChange,
  onGroupChange,
  onPageChange,
}: Props) {
  const [hasIdentifiedGroup, setHasIdentifiedGroup] = useState(false);

  const traits = useMemo(() => {
    if (!fetching && data?.identity && data?.trialStatus) {
      const { identity, trialStatus } = data;
      const { account, user } = identity;

      return {
        Account_created_at: account.createdAt,
        accountId: account.id,
        account_createdAt: account.createdAt,
        account_created_at_date: account.createdAt,
        account_flowsShown: account.flowsShown,
        accountName: account.name,
        account_id: account.id,
        account_isTrial: account.isTrial,
        account_isTrialExpired: account.isTrialExpired,
        account_stripePlanId: account.stripePlanId,
        account_stripeSubCreatedAt: account.stripeSubCreatedAt,
        account_testPassword: account.testPassword,
        company: account.name,
        createdAt: account.createdAt,
        created_at: account.createdAt,
        email: user.email,
        isAdmin: user.isAdmin,
        isInstalled: trialStatus.isWebInstalled,
        isInvitedUser: user.isInvitedUser,
        isTrial: account.isTrial,
        job: user.job,
        lastSeenTimestamp: user.lastSeenAt,
        sandboxUrl: account.sandboxUrl,
        sandboxSlug: account.sandboxSlug,
        signature: user.signature,
        userEmail: user.email,
        userId: user.id,
        userName: user.fullName,
      };
    }

    return null;
  }, [fetching, data]);

  const groupTraits = useMemo(() => {
    if (!fetching && data?.identity && data?.trialStatus) {
      const { identity, trialStatus } = data;
      const { account } = identity;

      return {
        account_createdAt: account.createdAt,
        account_flowsShown: account.flowsShown,
        account_isTrial: account.isTrial,
        account_isTrialExpired: account.isTrialExpired,
        account_stripePlanId: account.stripePlanId,
        account_stripeSubCreatedAt: account.stripeSubCreatedAt,
        company: account.name,
        id: account.id,
        isTrial: account.isTrial,
        sandboxUrl: account.sandboxUrl,
        sandboxSlug: account.sandboxSlug,
        stripePlanId: account.stripePlanId,
        lengthDays_trialByDays: trialStatus.lengthDays,
        isExpired_trialByDays: trialStatus.isExpired,
        expirationDate_trialByDays: trialStatus.expirationDate,
        isWebInstalled_trialByDays: trialStatus.isWebInstalled,
        groupProfileIdentifier: account.groupProfileIdentifier,
        userProfileIdentifier: account.userProfileIdentifier,
      };
    }

    return null;
  }, [fetching, data]);

  useEffect(() => {
    (window as any).AppcuesSettings = {
      ...(window as any).AppcuesSettings,
      userIdSignature: traits?.signature,
    };
  }, [traits]);

  useEffect(() => {
    if (!isSpoofing && groupTraits) {
      const { id, ...restGroupTraits } = groupTraits;
      onGroupChange(id, restGroupTraits);
      setHasIdentifiedGroup(true);
    }
  }, [groupTraits, setHasIdentifiedGroup, isSpoofing, onGroupChange]);

  useEffect(() => {
    if (!isSpoofing && traits && hasIdentifiedGroup) {
      const { userId, ...restTraits } = traits;
      onUserChange(userId, restTraits);
    }
  }, [traits, hasIdentifiedGroup, isSpoofing, onUserChange]);

  useEffect(() => {
    if (!isSpoofing) {
      onPageChange('App');
    }
  }, [location, isSpoofing, onPageChange]);
}
